import React from "react";
import { useDispatch } from "react-redux";
import { View, Text, StyleSheet, Dimensions, Platform } from "react-native";
import LhButton from "../../../../components/LhButton";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { resetTouchPointSurveyForm } from "../../../../store/slices/nhoSlice";

interface TouchPointWelcomeCardProps {
  title: string;
  onBackClick?: () => void;
  testID?: string;
}

const TouchPointWelcomeCard: React.FC<TouchPointWelcomeCardProps> = ({
  title,
  onBackClick,
  testID = "touchpoint-welcome-card",
}) => {
  // Redux hooks
  const dispatch = useDispatch();

  // Helper function to check if device is in mobile layout
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkIfMobile = () => {
      const windowWidth = Dimensions.get("window").width;
      const windowHeight = Dimensions.get("window").height;
      
      // Check if it's an iPad (iOS device with large screen)
      const isIPad = Platform.OS === "ios" && (windowWidth >= 768 || windowHeight >= 768);
      
      // Only consider it mobile for actual mobile devices (Android or small iOS devices)
      setIsMobile(
        (Platform.OS === "android" || (Platform.OS === "ios" && !isIPad)) || windowWidth < 768
      );
    };

    // Initial check
    checkIfMobile();

    // Add event listener for window resize (Web only)
    if (Platform.OS === "web") {
      window.addEventListener("resize", checkIfMobile);
    }

    // Clean up
    return () => {
      if (Platform.OS === "web") {
        window.removeEventListener("resize", checkIfMobile);
      }
    };
  }, []);

  // Handle back click with Redux reset
  const handleBackClick = React.useCallback(() => {
    console.log(
      "Resetting TouchPoint survey form data before navigating back..."
    );

    // Reset the TouchPoint survey form data in Redux
    dispatch(resetTouchPointSurveyForm());

    // Call the original onBackClick callback if provided
    if (onBackClick) {
      onBackClick();
    }
  }, [dispatch, onBackClick]);

  return (
    <View style={styles.welcomeCard} testID={testID}>
      <View
        style={[
          styles.welcomeContainer,
          isMobile && styles.welcomeContainerMobile,
        ]}
      >
        <View style={styles.welcomeLeftContent}>
          <MaterialIcons
            name="assignment"
            size={isMobile ? 20 : 24}
            color="#0066cc"
          />
          <Text
            style={[styles.welcomeText, isMobile && styles.welcomeTextMobile]}
          >
            {title}
          </Text>
        </View>

        {/* Back Button */}
        <View style={styles.buttonRow} testID={`${testID}-buttons`}>
          {onBackClick && (
            <LhButton
              text="Back to Dashboard"
              iconLeading="arrow-back"
              iconSize={20}
              onPress={handleBackClick}
              style={styles.backButton}
              textStyle={styles.buttonText}
              testID={`${testID}-back-button`}
            />
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  welcomeCard: {
    backgroundColor: "white",
    borderRadius: 8,
    marginTop: 16,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#0066CC",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  welcomeContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    flexWrap: "wrap",
  },
  welcomeContainerMobile: {
    flexDirection: "column",
    padding: 12,
  },
  welcomeLeftContent: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: Platform.OS === "web" ? 0 : 12,
  },
  welcomeText: {
    fontSize: 24,
    fontWeight: "500",
    color: "#212529",
    marginLeft: 12,
  },
  welcomeTextMobile: {
    fontSize: 20,
  },
  buttonRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    alignItems: "center",
  },
  buttonRowMobile: {
    marginTop: 8,
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
    gap: 10,
  },
  actionButton: {
    width: 200, // increased from 160
    minWidth: 200, // increased from 160
    alignSelf: "center",
    marginVertical: 5,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0066CC",
    paddingHorizontal: Dimensions.get("window").width < 600 ? 12 : 16,
    paddingVertical: Dimensions.get("window").width < 600 ? 8 : 10,
    borderRadius: 6,
    flexShrink: 0,
    width: 200, // Fixed width for all platforms
    minWidth: 200,
    justifyContent: "center",
  },
  buttonIcon: {
    color: "white",
    fontSize: Dimensions.get("window").width < 600 ? 14 : 16,
    marginRight: 4,
  },
  buttonText: {
    color: "white",
    fontSize: Dimensions.get("window").width < 600 ? 12 : 14,
    fontWeight: "500",
  },
});

export default TouchPointWelcomeCard;
